// Shared name matching for the in-browser search boxes (Family Tree search,
// Directory, and the person picker used by Cousin Lookup and Correspondence).
//
// Used for COMPARING only — stored names and what is shown on screen are never
// changed. It makes these all find each other:
//   - accents:     "Émile" = "Emile"
//   - apostrophes: "O'X" = "O’X" = "OX"   (straight, curly, or none)
//   - spaces:      "de X" = "deX" = "De X"
//   - letters with no accent mark: "Łukasz" = "Lukasz", "Straße" = "Strasse"

// Straight ' , curly ’ ‘ , modifier ʼ , backtick ` and acute ´
const APOSTROPHES = /['’‘ʼ`´]/g;

// Letters that have no accent to strip, so NFD leaves them alone. Kept in step
// with the server's Ask AI search (backend flask_app/name_match.py).
const FOLD = { ł: "l", ø: "o", ß: "ss", æ: "ae", œ: "oe", đ: "d", ð: "d", þ: "th", ı: "i" };
const FOLD_LETTERS = /[łøßæœđðþı]/g;

// Lowercase, accents removed, apostrophes removed. Spaces are kept so a search
// can still be split into words.
export const normalizeName = (str) =>
  (str || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(APOSTROPHES, "")
    .toLowerCase()
    .replace(FOLD_LETTERS, (ch) => FOLD[ch]);

// The same, with every space removed too ("de x" -> "dex").
export const squashName = (str) => normalizeName(str).replace(/\s+/g, "");

// Does the typed text match a name?
//   normalized: normalizeName(name), squashed: squashName(name) — worked out
//   once per person by the caller, not on every keystroke.
// A match is either every typed word found somewhere in the name (any order,
// as before), or the whole typed text, spaces removed, found in the name with
// its spaces removed — which is what lets "deX" find "de X".
export const nameMatches = (query, normalized, squashed) => {
  const words = normalizeName(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  if (words.every((word) => normalized.includes(word))) return true;
  const squashedQuery = words.join("");
  return squashed.includes(squashedQuery);
};

// Does every typed word START one of the name's words? "o'do" -> "odo", which
// starts "odowd" (from "O'Dowd") but only sits inside "theodore".
//   normalized: normalizeName(name), worked out once by the caller.
export const nameStartsWith = (query, normalized) => {
  const words = normalizeName(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return false;
  const nameWords = (normalized || "").split(/\s+/).filter(Boolean);
  return words.every((word) => nameWords.some((n) => n.startsWith(word)));
};

// Put the names whose words START with the typed text first, then the rest.
// Same items, nothing dropped; each group keeps its original order.
//   getNormalized(item) returns that item's normalizeName(name).
export const rankByNameStart = (query, items, getNormalized) => {
  const first = [];
  const rest = [];
  items.forEach((item) => {
    (nameStartsWith(query, getNormalized(item)) ? first : rest).push(item);
  });
  return first.concat(rest);
};
